/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\providers\GoogleVoiceTestPanel.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the AI Integration module.
----------------------------------------------------------------*/


import React from 'react';
import { Volume2, Loader2, Play, Download } from 'lucide-react';
import { Label } from '../../../../components/ui/label';
import { Button } from '../../../../components/ui/button';
import { useI18n } from '../../../../hooks/useI18n';

interface GoogleVoiceTestPanelProps {
    audioRef: React.RefObject<HTMLAudioElement>;
    audioUrl: string | null;
    audioError: string | null;
    isGeneratingAudio: boolean;
    testText: string;
    onTestTextChange: (v: string) => void;
    onGenerate: () => void;
    onPlay: () => void;
}

export function GoogleVoiceTestPanel({
    audioRef,
    audioUrl,
    audioError,
    isGeneratingAudio,
    testText,
    onTestTextChange,
    onGenerate,
    onPlay,
}: GoogleVoiceTestPanelProps) {
    const { t } = useI18n();

    const handleDownload = () => {
        if (!audioUrl) return;
        const link = document.createElement('a');
        link.href = audioUrl;
        link.download = 'audio_test.wav';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <>
            {/* Hidden audio element */}
            <audio ref={audioRef} src={audioUrl || undefined} autoPlay style={{ display: 'none' }} />

            {/* Text area */}
            <div>
                <Label className="text-sm font-medium text-foreground mb-2 block">
                    {t('aiProfiles.voiceConfig.textToConvert') || 'Texto a convertir:'}
                </Label>
                <textarea
                    value={testText}
                    onChange={(e) => onTestTextChange(e.target.value)}
                    placeholder={t('aiProfiles.voiceConfig.enterTextPlaceholder') || 'Ingresa el texto a convertir a voz...'}
                    className="w-full px-3 py-2 text-sm bg-card border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-y text-foreground"
                    rows={2}
                />
            </div>

            {/* Error */}
            {audioError && !isGeneratingAudio && (
                <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-3">
                    <p className="text-sm text-red-600 dark:text-red-400">{audioError}</p>
                </div>
            )}

            {/* Action buttons */}
            <div className="flex items-center gap-2 p-3 bg-primary/5 border border-primary/20 rounded-lg">
                <Button
                    type="button"
                    onClick={onGenerate}
                    disabled={isGeneratingAudio || !testText.trim()}
                    variant="default"
                    className="flex-1"
                >
                    {isGeneratingAudio ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('aiProfiles.googleTts.generatingAudio') || 'Generando...'}</>
                    ) : (
                        <><Volume2 className="h-4 w-4 mr-2" />{t('aiProfiles.googleTts.convertToAudio') || 'Convertir a Audio'}</>
                    )}
                </Button>
                <Button
                    type="button"
                    onClick={onPlay}
                    disabled={!audioUrl || isGeneratingAudio}
                    variant="outline"
                    className="border-green-500 text-green-600 hover:bg-green-500/10 hover:text-green-600"
                >
                    <Play className="h-4 w-4 mr-2" />
                    {t('aiProfiles.googleTts.playAudio') || 'Reproducir'}
                </Button>
                <Button
                    type="button"
                    onClick={handleDownload}
                    disabled={!audioUrl || isGeneratingAudio}
                    variant="outline"
                    className="border-cyan-500 text-cyan-600 hover:bg-cyan-500/10 hover:text-cyan-600"
                >
                    <Download className="h-4 w-4 mr-2" />
                    {t('aiProfiles.googleTts.downloadAudio') || 'Descargar'}
                </Button>
            </div>
        </>
    );
}
